import { zodResolver } from "@hookform/resolvers/zod";
import { type StytchAPIError, useStytch } from "@stytch/react";
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { AuthFlowContinueButton } from "@/components/ui/auth-flow/auth-flow-continue-button";
import { AuthFlowTitle } from "@/components/ui/auth-flow/auth-flow-title";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
} from "@/components/ui/form/form";
import { InputPassword } from "@/components/ui/input/input-password";
import { useReturnToNavigation } from "@/hooks/use-return-to-navigation";
import {
  captureAuthError,
  trackFirstAccessPasswordSet,
  trackFirstAccessStepViewed,
} from "@/lib/auth/auth-tracking";
import {
  type FirstAccessPasswordFormFields,
  firstAccessPasswordSchema,
} from "@/lib/form-schemas/first-access-password-schema";
import { guardFirstAccessEmail } from "@/lib/guards/first-access-guard";
import { StytchErrorType } from "@/lib/mappers/stytch-error";

const FirstAccessDefinePasswordPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = useSearch({ from: "/auth/first-access/define-password" });
  const stytch = useStytch();
  const [isLoading, setIsLoading] = useState(false);
  const { navigateToReturnToOrFallback } = useReturnToNavigation();

  useEffect(() => {
    trackFirstAccessStepViewed("definir-senha");
  }, []);

  const form = useForm<FirstAccessPasswordFormFields>({
    resolver: zodResolver(firstAccessPasswordSchema),
    mode: "onChange",
    defaultValues: {
      password: "",
    },
  });

  const isPasswordValid = form.formState.isValid;

  const handleContinue = async (data: FirstAccessPasswordFormFields): Promise<void> => {
    setIsLoading(true);

    try {
      await stytch.passwords.resetBySession({
        password: data.password,
        session_duration_minutes: 60,
      });

      trackFirstAccessPasswordSet("sucesso");
      navigateToReturnToOrFallback("/auth/first-access/complete");
    } catch (error) {
      const stytchError = error as StytchAPIError;

      trackFirstAccessPasswordSet("falha", error);

      if (
        stytchError.status_code === 401 ||
        stytchError.error_type === StytchErrorType.SessionTooOldToResetPassword
      ) {
        void navigate({ to: "/auth/first-access/document" });
        return;
      }

      captureAuthError(error, { flow: "primeiro-acesso", step: "definir-senha" });
      showUnexpectedErrorDrawer();
    } finally {
      setIsLoading(false);
    }
  };

  const showUnexpectedErrorDrawer = (): void => {
    void navigate({
      to: "/auth/first-access/define-password",
      search: { action: "error" },
    });
  };

  const hideUnexpectedErrorDrawer = (): void => {
    if (searchParams.action === "error") {
      router.history.back();
    }
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={0.75} />
      </FlowHeader>

      {/* Page content */}
      <FormRoot {...form}>
        <FormHandlerSubmit
          handleSubmit={handleContinue}
          className="flex flex-col flex-1 p-6 gap-6 w-full"
        >
          <AuthFlowTitle>
            <AuthFlowTitle.Heading>Defina sua senha de acesso</AuthFlowTitle.Heading>
            <AuthFlowTitle.Sub className="text-neutral-600">
              Crie uma senha segura seguindo os critérios abaixo
            </AuthFlowTitle.Sub>
          </AuthFlowTitle>

          <div className="flex-1">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <InputPassword
                    title="Senha"
                    placeholder="••••••••"
                    defaultValue={field.value}
                    showStrengthChecks
                    onChange={(value) => {
                      if (typeof value === "string") {
                        field.onChange(value);
                      }
                    }}
                  />
                </FormItem>
              )}
            />
          </div>

          <AuthFlowContinueButton
            type="submit"
            loading={isLoading}
            disabled={!isPasswordValid}
          />
        </FormHandlerSubmit>
      </FormRoot>

      <UnexpectedErrorDrawer
        open={searchParams.action === "error"}
        setOpen={(isOpen) => {
          if (isOpen) showUnexpectedErrorDrawer();
          else hideUnexpectedErrorDrawer();
        }}
      />
    </AuthPageLayout>
  );
};

const firstAccessDefinePasswordSearchSchema = z.object({
  action: z.string().nullable().optional(),
});

export const Route = createFileRoute("/auth/first-access/define-password")({
  beforeLoad: () => {
    guardFirstAccessEmail();
  },
  component: FirstAccessDefinePasswordPage,
  validateSearch: firstAccessDefinePasswordSearchSchema,
});
